// Conjuntos que convergen (§5.4): agregar y quitar elementos sobre el valor
// actual, no sobre el que vio el usuario. Dos personas que agregan etiquetas
// sin conexión suman las dos, sin conflicto.

import type { SetDelta } from "./types";

const keyOf = (item: unknown) => JSON.stringify(item);

/** Elementos de un valor guardado como arreglo o como arreglo en JSON. */
export function decodeSet(value: unknown): unknown[] {
  if (Array.isArray(value)) return value;
  if (typeof value === "string" && value.trim().startsWith("[")) {
    try {
      const parsed: unknown = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

/**
 * Aplica un delta a un valor. Conserva el orden existente, agrega al final lo
 * nuevo, quita sin importar el orden. Vacío → `undefined` (campo sin valor).
 */
export function applySetDelta(value: unknown, delta: SetDelta): unknown {
  const current = decodeSet(value);
  const remove = new Set((delta.remove ?? []).map(keyOf));
  const seen = new Set<string>();
  const next: unknown[] = [];
  for (const item of [...current, ...(delta.add ?? [])]) {
    const key = keyOf(item);
    if (remove.has(key) || seen.has(key)) continue;
    seen.add(key);
    next.push(item);
  }
  if (next.length === 0) return undefined;
  const encoding = delta.encoding ?? (typeof value === "string" ? "json" : "array");
  return encoding === "json" ? JSON.stringify(next) : next;
}

/** Delta que lleva de `before` a `after` (para armar operaciones desde un formulario). */
export function diffSet(before: unknown, after: unknown): SetDelta {
  const a = decodeSet(before);
  const b = decodeSet(after);
  const inA = new Set(a.map(keyOf));
  const inB = new Set(b.map(keyOf));
  return {
    add: b.filter((item) => !inA.has(keyOf(item))),
    remove: a.filter((item) => !inB.has(keyOf(item))),
    encoding: typeof after === "string" || (after === undefined && typeof before === "string") ? "json" : "array",
  };
}
